import { createContext, Fragment, useContext } from 'react'
import { fillGuideCopy } from '../../lib/guideCopy'
import { parseBlocks, type Block, type Inline } from '../../lib/guideMarkdown'
import { GUIDE_WIDGETS } from './GuideWidgets'

/**
 * ONE SECTION OF A GUIDE, DRAWN FROM ITS MARKDOWN (339, HLP-45).
 *
 * The body comes from the database as written in guides/<key>.md, quotes
 * unfilled; `{{KEY}}` is filled here from lib/guideCopy.ts - the screens'
 * own constants - so a guide always says what the button says today. The
 * classes are the store guides' own (`guide__*`, hub.css), so the two guides
 * look as they did inside Shop and stock.
 *
 * Pure: no fetch, no effect, so the suite renders a whole guide (062).
 */
/** The locale of the text being drawn: a Spanish section fills its quotes
 *  from the es column; a section shown in English (113's fallback) in English. */
const GuideLocale = createContext('en-CA')

export function GuideBody({ body, locale = 'en-CA' }: { body: string; locale?: string }) {
  return (
    <GuideLocale.Provider value={locale}>
      {parseBlocks(body).map((b, i) => (
        <GuideBlock key={i} block={b} />
      ))}
    </GuideLocale.Provider>
  )
}

function GuideBlock({ block: b }: { block: Block }) {
  switch (b.t) {
    case 'p':
      return <p><Ins xs={b.v} /></p>
    case 'ul':
      return <ul>{b.items.map((x, i) => <li key={i}><Ins xs={x} /></li>)}</ul>
    case 'steps':
      return <ol className="guide__steps">{b.items.map((x, i) => <li key={i}><Ins xs={x} /></li>)}</ol>
    case 'note':
      return (
        <div className={`guide__note guide__note--${b.tone === 'note' ? 'info' : b.tone}`}>
          <p className="guide__notelabel"><Ins xs={b.label} /></p>
          <p><Ins xs={b.v} /></p>
        </div>
      )
    case 'dl':
      return (
        <dl className="guide__terms">
          {b.items.map((x, i) => (
            <Fragment key={i}>
              <dt><Ins xs={x.term} /></dt>
              <dd><Ins xs={x.def} /></dd>
            </Fragment>
          ))}
        </dl>
      )
    case 'table':
      return (
        <div className="guide__tablewrap">
          <table className="admin__table">
            <thead>
              <tr>{b.head.map((h, i) => <th key={i} scope="col"><Ins xs={h} /></th>)}</tr>
            </thead>
            <tbody>
              {b.rows.map((r, i) => (
                <tr key={i}>{r.map((c, j) => <td key={j}><Ins xs={c} /></td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    case 'q':
      return (
        <details className="guide__q">
          <summary><Ins xs={b.q} /></summary>
          <div><p><Ins xs={b.a} /></p></div>
        </details>
      )
    case 'columns':
      return (
        <div className="guide__lists">
          {b.cols.map((c, i) => (
            <div key={i}>
              <h4><Ins xs={c.title} /></h4>
              <ul>{c.items.map((x, j) => <li key={j}><Ins xs={x} /></li>)}</ul>
            </div>
          ))}
        </div>
      )
    case 'widget': {
      const w = GUIDE_WIDGETS[b.name]
      // The suite fails on an unknown widget; on screen it is simply absent
      // rather than a broken box.
      return w ? <>{w()}</> : null
    }
  }
}

function Ins({ xs }: { xs: Inline[] }) {
  const locale = useContext(GuideLocale)
  return (
    <>
      {xs.map((x, i) => {
        switch (x.t) {
          case 'text':
            return <Fragment key={i}>{x.v}</Fragment>
          case 'b':
            return <b key={i}><Ins xs={x.v} /></b>
          case 'code':
            return <code key={i}>{x.v}</code>
          case 'ui':
            return <span key={i} className="guide__ui"><Ins xs={x.v} /></span>
          case 'pill':
            return <span key={i} className="pill pill--draft">{x.v}</span>
          case 'link':
            return <a key={i} href={x.href}><Ins xs={x.v} /></a>
          case 'copy':
            return <Fragment key={i}>{fillGuideCopy(x.key, x.arg, locale)}</Fragment>
        }
      })}
    </>
  )
}
